import type { DTO } from '@/type-utils';
import Model from '../Model';
import type { IWallet } from './IWallet';
import { SavedPaymentModel } from './SavedPaymentModel';

/**
 * Represents a wallet that contains saved payment methods.
 *
 * We here change the structure a little from the original {@link IWallet} interface.
 * Instead of having a key-value pair where the key is a UUID and the value is an
 * instance of {@link ISavedPayment}, we use a model that contains an array of
 * {@link SavedPaymentModel} instances.
 *
 * This allows us to easily iterate over the saved payments and access their properties
 * without having to deal with the key-value structure.
 */
export class WalletModel extends Model<DTO<IWallet>> {
  private _savedPayments: Array<SavedPaymentModel>;

  /** @inheritdoc */
  public get savedPayments(): Array<SavedPaymentModel> {
    return this._savedPayments;
  }

  /**
   * Converts a wallet object (key-value pair) to an array of saved payment models.
   *
   * @param wallet - The wallet object to convert, which is a DTO of type IWallet.
   * @returns An array of {@link SavedPaymentModel} instances.
   */
  private convertWalletSetToArray(
    wallet: DTO<IWallet>
  ): Array<SavedPaymentModel> {
    return Object.entries(wallet).map(
      ([key, value]) =>
        new SavedPaymentModel({
          ...value,
          // The WalletKeyUUID is used as the UUID for the saved payment
          uuid: key
        })
    );
  }

  /**
   * Converts an array of saved payment models to a wallet object (key-value pair).
   *
   * @param wallet - The array of saved payment models to convert.
   * @returns A DTO of type IWallet, where each key is the UUID of the saved payment
   * and the value is the DTO of the saved payment model.
   */
  private convertWalletArrayToSet(
    wallet: Array<SavedPaymentModel>
  ): DTO<IWallet> {
    return wallet.reduce<IWallet>((acc, savedPayment) => {
      acc[savedPayment.uuid] = savedPayment.toDTO();
      return acc;
    }, {});
  }

  /** @inheritdoc */
  public constructor(data: DTO<IWallet>) {
    super(data);

    this._savedPayments = this.convertWalletSetToArray(data);
  }

  /** @inheritdoc */
  public override toDTO(): DTO<IWallet> {
    return this.convertWalletArrayToSet(this._savedPayments);
  }
}
